import React from 'react';
import ReactDOM from 'react-dom';
import {ApolloClient} from 'apollo-client';
import {ApolloLink} from 'apollo-link';
import {HttpLink} from 'apollo-link-http';
import {onError} from 'apollo-link-error';

import AbacusClientUtils from '@homeaway/abacus-client-utils';
import {reportError} from '@homeaway/react-error-reporter';
import {TrackFocus} from '@homeaway/a11y-tools';

import {createStore} from './store';
import Root from './components/Root/Root';
import {logExperiments} from './utils/abacus';
import haWrapper from './utils/haWrapper';
import logger from './utils/clientLogger';
import {PrefetchLink} from './utils/PrefetchLink';
import {sendCustomAnalytics} from './utils/loadTimeAnalyticsWrapper';
import {HA_SERP_NUMBER_OF_HITS} from '../universal/abacus/experiments';
import {trigger as triggerEdap} from './utils/edapWrapper';
import {createCache} from './graphql/cache';
import Application from './components/Application/Application';
import loadLocaleData from './utils/loadLocaleData';

// Let webpack know where to load files from in production envs.
// This is primarily for code-splitting scenarios and for images.
if (!module.hot) {
    __webpack_public_path__ = `${window.cdnUrl}/`; // eslint-disable-line no-undef, camelcase
}

if (process.env.NODE_ENV !== 'production') {
    if (window.__DEBUG_COMPONENT_UPDATES__) {
        const {whyDidYouUpdate} = require('why-did-you-update');
        whyDidYouUpdate(React);
    }
}

/**
 * Main client-side entry point. This is not used during
 * server-side rendering, only after the javascript bundle
 * has been loaded into the browser.
 */

const trackFocus = new TrackFocus();

// Initial state as set by the server-side render
const initialState = window.__INITIAL_STATE__;
const initialApolloState = window.__INITIAL_APOLLO_STATE__;

// Initialize redux store
const store = createStore({initialState});

const siteContext = window.__SITE_CONTEXT__;

const {abacus, system} = store.getState();

// // Get Intl Data
const localeLoader = loadLocaleData(system.fullLocale);

// initialize graphql client
const loggerLink = new ApolloLink((operation, forward) => {
    logger.captureBreadcrumb({
        message: `starting request for ${operation.operationName}`,
        category: 'graphQL',
        data: {
            name: operation.operationName,
            variables: operation.variables
        }
    });
    return forward(operation).map((data) => {
        logger.captureBreadcrumb({
            message: `ending request for ${operation.operationName}`,
            category: 'graphQL',
            data: {
                name: operation.operationName,
                variables: operation.variables
            }
        });
        return data;
    });
});

const errorLink = onError(({graphQLErrors, networkError}) => {
    if (graphQLErrors && system && system.environment !== 'production') {
        graphQLErrors.forEach((err) => {
            const {message, locations, path} = err || {};
            // eslint-disable-next-line no-console
            console.warn(
                `[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(
                    locations
                )}, Path: ${path}`
            );
        });
    }
    if (networkError) {
        logger.captureException(`[Network error]: ${networkError}`, {
            fingerprint: ['Type: NetworkError', `Message: ${networkError}`]
        });
    }
});

const TRIPBOARD_OPERATIONS = [
    'addUnitToTripBoard',
    'paginatedTripBoards',
    'createTripBoard',
    'removeUnitFromTripBoards',
    'updateStayOnTripBoard'
];

const isTripBoardOperation = (operation) =>
    TRIPBOARD_OPERATIONS.includes(operation.operationName);

const prefetcherLink = new PrefetchLink();

const linkHeaders = {
    'x-csrf-token': haWrapper.crumb,
    'x-homeaway-site': store.getState().system.siteName
};

const client = new ApolloClient({
    link: ApolloLink.from([
        errorLink,
        prefetcherLink,
        loggerLink,
        ApolloLink.split(
            isTripBoardOperation,
            new HttpLink({
                uri: '/traveler/scratchpad/api/graphql',
                credentials: 'same-origin',
                headers: linkHeaders
            }),
            new HttpLink({
                uri: '/serp/g',
                credentials: 'same-origin',
                headers: linkHeaders,
                includeExtensions: true
            })
        )
    ]),
    cache: createCache().restore(initialApolloState)
});

// set up abacus experiment cache
AbacusClientUtils.initialize(abacus);

logExperiments(abacus, [HA_SERP_NUMBER_OF_HITS]);

// log direct entry traffic
triggerEdap('generic.event', {
    eventaction: 'direct entry traffic',
    eventcategory: 'search',
    eventvalue: !!(system && system.directEntry)
});

haWrapper.userTiming.measure('navigationStart');

// measure full page load
window.addEventListener('load', () => {
    const pageload = 'pageload';

    haWrapper.userTiming.measure(pageload);

    sendCustomAnalytics(pageload, 'measure', (analytics) => ({
        [pageload]: analytics.duration
    }));
});

window.gm_authFailure = function handleMapAuthFailure() {
    if (window.Raven && window.Raven.captureMessage) {
        logger.captureMessage(
            'Google Maps "gm_authFailure" - Please look at breadcrumbs for exact error.'
        );
    }
    reportError({name: 'Oops unable to load map'});
};

// set up keyboard focus tracking
trackFocus.bindEvents();

localeLoader.then(() => {
    ReactDOM.hydrate(
        <Root
            store={store}
            client={client}
            abacus={abacus}
            siteContext={siteContext}
        >
            <Application />
        </Root>,
        document.getElementById('root')
    );
});
